import { InputNumber } from "@/components/InputNumber";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  datePickerStandardProps,
  selectStandardProps,
} from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { dateToIsoDateString, toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, DatePicker, Form, Input, Modal, Row, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateSpecialCommissionsRule, useUpdateSpecialCommissionsRule } from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { Agent } from "../../../../agent/types";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import { SpecialCommissionsSettingsRuleType } from "../../enums";
import { CreateUpdateSpecialCommissionsSettingsRule, SpecialCommissionsSettingsRule } from "../../types";
import { isRateRuleType } from "../../utils";

type GetDateDataType = {
  type: SpecialCommissionsSettingsRuleType;
  startDate?: string;
  endDate?: string;
};

const getCommissionSettingsRuleDate = (
  configuration: "startDate" | "endDate",
  { type, startDate, endDate }: GetDateDataType,
) => {
  const isOneTime = type === SpecialCommissionsSettingsRuleType.ONE_TIME;
  const MONTH = "month";

  if (configuration === "startDate") {
    if (isOneTime && startDate) {
      return dateToIsoDateString(dayjs.utc(startDate).startOf(MONTH));
    }

    return startDate;
  } else {
    if (isOneTime && startDate) {
      return dateToIsoDateString(dayjs.utc(startDate).endOf(MONTH));
    }

    return endDate;
  }
};

const colSpanSmall = 4;
const colSpan = 6;
const colSpanBig = 8;

interface Props {
  open: boolean;
  rule?: SpecialCommissionsSettingsRule;
  agent: Agent;
  onFormCancel: () => void;
}

export const SpecialCommissionsSettingsRuleForm = ({ open, rule, agent, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const createSpecialCommissionsRule = useCreateSpecialCommissionsRule();
  const updateSpecialCommissionsRule = useUpdateSpecialCommissionsRule();

  const [form] = Form.useForm<CreateUpdateSpecialCommissionsSettingsRule>();
  const specialCommissionsSettingsRuleType = Form.useWatch("type", form);
  const startDate = Form.useWatch("startDate", form);
  const endDate = Form.useWatch("endDate", form);

  useEffect(() => {
    if (open && rule) {
      form.setFieldsValue({
        ...rule,
        targetAgentId: rule.targetAgent?.id,
        payerId: rule.payer.id,
      });
    }
  }, [rule, open, form]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      const processedValues = {
        ...values,
        startDate: getCommissionSettingsRuleDate("startDate", {
          type: values.type,
          startDate: values.startDate,
          endDate: values.endDate,
        }),
        endDate: getCommissionSettingsRuleDate("endDate", {
          type: values.type,
          startDate: values.startDate,
          endDate: values.endDate,
        }),
      };

      if (rule) {
        updateSpecialCommissionsRule.mutate(
          { id1: agent.id, id2: rule.id, object: processedValues },
          {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "commissions.special.attrs", error.violations),
          },
        );
      } else {
        createSpecialCommissionsRule.mutate(
          { id: agent.id, object: processedValues },
          {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "commissions.special.attrs", error.violations),
          },
        );
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.LARGE}
      open={open}
      title={rule ? t("commissions.special.titles.updateRule") : t("commissions.special.titles.createRule")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createSpecialCommissionsRule.isPending || updateSpecialCommissionsRule.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="specialCommissionsSettingsRuleForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item
              name="code"
              label={t("commissions.special.attrs.code")}
              rules={[validations.notBlank, validations.size(1, 64)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={16}>
            <Form.Item
              name="name"
              label={t("commissions.special.attrs.name")}
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpanBig}>
            <Form.Item name="type" label={t("commissions.special.enums.ruleType._label")} rules={[validations.notNull]}>
              <Select
                {...selectStandardProps}
                options={Object.keys(SpecialCommissionsSettingsRuleType).map((type) => ({
                  value: type,
                  label: t("commissions.special.enums.ruleType." + type),
                }))}
              />
            </Form.Item>
          </Col>

          {specialCommissionsSettingsRuleType && (
            <>
              {specialCommissionsSettingsRuleType === SpecialCommissionsSettingsRuleType.ONE_TIME ? (
                <Col span={colSpan}>
                  <Form.Item
                    name="startDate"
                    label={t("commissions.special.helpers.oneTimeRuleDate")}
                    rules={[validations.notNull]}
                    {...datePickerFormItemProps}
                  >
                    <DatePicker
                      {...datePickerStandardProps}
                      format="MMMM YYYY"
                      placeholder={undefined}
                      allowClear={false}
                      picker="month"
                    />
                  </Form.Item>
                </Col>
              ) : (
                <>
                  <Col span={colSpan}>
                    <Form.Item
                      name="startDate"
                      label={t("commissions.special.attrs.startDate")}
                      rules={[validations.none]}
                      {...datePickerFormItemProps}
                    >
                      <DatePicker {...datePickerClearableProps} />
                    </Form.Item>
                  </Col>

                  <Col span={colSpan}>
                    <Form.Item
                      name="endDate"
                      label={t("commissions.special.attrs.endDate")}
                      rules={[
                        startDate
                          ? validations.notBefore(startDate, t("commissions.special.attrs.startDate"))
                          : validations.none,
                      ]}
                      {...datePickerFormItemProps}
                    >
                      <DatePicker
                        {...datePickerClearableProps}
                        defaultPickerValue={toDate(endDate || startDate)}
                        minDate={toDate(startDate)}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              <Col span={colSpanSmall}>
                <Form.Item
                  name="suspended"
                  rules={[validations.none]}
                  className="form-item-without-label"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Checkbox>{t("commissions.special.attrs.suspended")}</Checkbox>
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpanBig}>
            <Form.Item name="payerId" label={t("commissions.special.attrs.payerId")} rules={[validations.notNull]}>
              <AgentSelect optionsProps={{ defaultOption: rule?.payer, excludedIds: [agent.id] }} />
            </Form.Item>
          </Col>

          {specialCommissionsSettingsRuleType &&
            (isRateRuleType(specialCommissionsSettingsRuleType) ? (
              <>
                <Col span={colSpan}>
                  <Form.Item
                    name="rate"
                    label={t("commissions.special.attrs.rate")}
                    rules={[validations.notNull, validations.minNumber(0.01), validations.maxNumber(100)]}
                  >
                    <InputNumber type="decimal" addon="percentage" />
                  </Form.Item>
                </Col>

                <Col span={10}>
                  <Form.Item
                    name="targetAgentId"
                    label={
                      specialCommissionsSettingsRuleType === SpecialCommissionsSettingsRuleType.NETTO_POINTS_RATE
                        ? t("commissions.special.helpers.nettoPointsTargetAgent")
                        : t("commissions.special.helpers.commissionAmountTargetAgent")
                    }
                    rules={[validations.notNull]}
                  >
                    <AgentSelect />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <Col span={colSpan}>
                <Form.Item
                  name="amount"
                  label={t("commissions.special.attrs.amount")}
                  rules={[validations.notNull, validations.minNumber(0.01)]}
                >
                  <InputNumber type="decimal" addon="euro" />
                </Form.Item>
              </Col>
            ))}
        </Row>
      </Form>
    </Modal>
  );
};
